import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiPower,
  FiGlobe,
  FiSettings,
  FiUsers,
  FiHome,
  FiTrendingUp
} from 'react-icons/fi';
import superadminService from '../../../services/superadminService';
import { Tenant } from '../../../types/tenant';
import { useNotification } from '../../../contexts/NotificationContext';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h2 {
    font-size: 2rem;
    color: #333;
    margin: 0;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  .icon {
    width: 48px;
    height: 48px;
    background: #f0f0f0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    color: #ff6b6b;
    font-size: 1.5rem;
  }
  
  .value {
    font-size: 2rem;
    font-weight: bold;
    color: #333;
    margin-bottom: 0.25rem;
  }
  
  .label {
    color: #666;
    font-size: 0.875rem;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #ff6b6b;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #ff5252;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const TenantCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  
  h3 {
    margin: 0 0 0.5rem 0;
    color: #333;
    font-size: 1.25rem;
  }
  
  .domain {
    color: #666;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
  
  .info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    font-size: 0.875rem;
    
    .label {
      color: #666;
    }
    
    .value {
      color: #333;
      font-weight: 500;
    }
  }
`;

const PlanBadge = styled.span<{ plan: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.plan) {
      case 'trial': return '#ffd93d';
      case 'basic': return '#6bcf7f';
      case 'professional': return '#4e89e3';
      case 'enterprise': return '#b983ff';
      default: return '#ccc';
    }
  }};
  color: ${props => props.plan === 'trial' ? '#333' : 'white'};
`;

const StatusBadge = styled.span<{ active: boolean }>`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => props.active ? '#6bcf7f' : '#ff6b6b'};
  color: white;
`;

const CardActions = styled.div`
  padding: 1rem 1.5rem;
  background-color: #f8f8f8;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #f0f0f0;
    border-color: #ccc;
  }
  
  svg {
    font-size: 1rem;
    color: #666;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
  
  svg {
    font-size: 4rem;
    color: #ddd;
    margin-bottom: 1rem;
  }
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    color: #333;
  }
  
  p {
    margin-bottom: 2rem;
  }
`;

const SuperadminTenants: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    trial: 0,
    paid: 0
  });
  
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await superadminService.getTenants();
      setTenants(data);
      
      // Calculate stats
      const stats = {
        total: data.length,
        active: data.filter((t: Tenant) => t.is_active).length,
        trial: data.filter((t: Tenant) => t.plan_type === 'trial').length,
        paid: data.filter((t: Tenant) => ['basic', 'professional', 'enterprise'].includes(t.plan_type)).length
      };
      setStats(stats);
    } catch (error) {
      showNotification('Erro ao carregar tenants', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (tenant: Tenant, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (tenant.is_active) {
        await superadminService.deleteTenant(tenant.id);
        showNotification('Tenant desativado com sucesso', 'success');
      } else {
        await superadminService.activateTenant(tenant.id);
        showNotification('Tenant ativado com sucesso', 'success');
      }
      loadTenants();
    } catch (error) {
      showNotification('Erro ao alterar status do tenant', 'error');
    }
  };

  const handleEdit = (tenantId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/superadmin/tenants/${tenantId}/edit`);
  };

  const handleConfig = (tenantId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/superadmin/tenants/${tenantId}/config`);
  };

  const handleCardClick = (tenantId: string) => {
    navigate(`/superadmin/tenants/${tenantId}`);
  };

  const formatDate = (date: string) => {
    return format(new Date(date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  };

  return (
    <Container>
      <Header>
        <h2>Gerenciar Tenants</h2>
        <AddButton onClick={() => navigate('/superadmin/tenants/new')}>
          <FiPlus />
          Novo Tenant
        </AddButton>
      </Header>

      <Stats>
        <StatCard>
          <div className="icon">
            <FiHome />
          </div>
          <div className="value">{stats.total}</div>
          <div className="label">Total de Tenants</div>
        </StatCard>
        
        <StatCard>
          <div className="icon">
            <FiPower />
          </div>
          <div className="value">{stats.active}</div>
          <div className="label">Tenants Ativos</div>
        </StatCard>
        
        <StatCard>
          <div className="icon">
            <FiUsers />
          </div>
          <div className="value">{stats.trial}</div>
          <div className="label">Em Período de Teste</div>
        </StatCard>
        
        <StatCard>
          <div className="icon">
            <FiTrendingUp />
          </div>
          <div className="value">{stats.paid}</div>
          <div className="label">Planos Pagos</div>
        </StatCard>
      </Stats>

      {loading ? (
        <EmptyState>
          <p>Carregando tenants...</p>
        </EmptyState>
      ) : tenants.length === 0 ? (
        <EmptyState>
          <FiUsers />
          <h3>Nenhum tenant cadastrado</h3>
          <p>Comece criando o primeiro tenant do sistema.</p>
          <AddButton onClick={() => navigate('/superadmin/tenants/new')}>
            <FiPlus />
            Criar Primeiro Tenant
          </AddButton>
        </EmptyState>
      ) : (
        <Grid>
          {tenants.map(tenant => (
            <TenantCard key={tenant.id} onClick={() => handleCardClick(tenant.id)}>
              <CardHeader>
                <h3>{tenant.name}</h3>
                <div className="domain">
                  <FiGlobe />
                  {tenant.domain || tenant.subdomain || 'Sem domínio'}
                </div>
              </CardHeader>
              
              <CardBody>
                <div className="info-row">
                  <span className="label">Plano:</span>
                  <PlanBadge plan={tenant.plan_type}>
                    {tenant.plan_type.toUpperCase()}
                  </PlanBadge>
                </div>
                
                <div className="info-row">
                  <span className="label">Status:</span>
                  <StatusBadge active={tenant.is_active}>
                    {tenant.is_active ? 'Ativo' : 'Inativo'}
                  </StatusBadge>
                </div>
                
                <div className="info-row">
                  <span className="label">Criado em:</span>
                  <span className="value">{formatDate(tenant.created_at)}</span>
                </div>
                
                {tenant.trial_ends_at && (
                  <div className="info-row">
                    <span className="label">Teste expira em:</span>
                    <span className="value">{formatDate(tenant.trial_ends_at)}</span>
                  </div>
                )}
              </CardBody>
              
              <CardActions>
                <ActionButton
                  onClick={(e) => handleConfig(tenant.id, e)}
                  title="Configurações"
                >
                  <FiSettings />
                </ActionButton>
                
                <ActionButton
                  onClick={(e) => handleEdit(tenant.id, e)}
                  title="Editar"
                >
                  <FiEdit />
                </ActionButton>
                
                <ActionButton
                  onClick={(e) => handleToggleStatus(tenant, e)}
                  title={tenant.is_active ? 'Desativar' : 'Ativar'}
                >
                  <FiPower />
                </ActionButton>
              </CardActions>
            </TenantCard>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default SuperadminTenants;